/**
 * Machine-readable Buy Sources failure reasons (internal diagnostics).
 * User-facing copy stays generic ("Source search unavailable").
 */

export const BUY_SOURCES_FAILURE_REASON = Object.freeze({
  PROVIDER_TIMEOUT: "provider_timeout",
  PROVIDER_HTTP_ERROR: "provider_http_error",
  PROVIDER_RATE_LIMITED: "provider_rate_limited",
  REQUEST_BUDGET_EXHAUSTED: "request_budget_exhausted",
  FRONTEND_ABORTED: "frontend_aborted",
  MISSING_QUERY_IDENTITY: "missing_query_identity",
  CACHE_FAILURE: "cache_failure",
  BACKEND_ERROR: "backend_error",
  // Google source provider disabled / unavailable server-side (not transient).
  PROVIDER_UNAVAILABLE: "provider_unavailable",
  UNKNOWN: "unknown",
});

/** Backend marks results with ``provider_status: "unavailable"`` when no Google
 * provider can run; the item is neither a match nor a no-match. */
export function isProviderUnavailableResult(data) {
  return String(data?.provider_status || "").toLowerCase() === "unavailable";
}

export function providerUnavailableDetail(data) {
  return {
    message: "Source search unavailable.",
    reason: BUY_SOURCES_FAILURE_REASON.PROVIDER_UNAVAILABLE,
    buy_sources_failure_reason: BUY_SOURCES_FAILURE_REASON.PROVIDER_UNAVAILABLE,
    provider_status_class: "unavailable",
    request_id: data?.request_id || null,
    walmart_item_id: data?.walmart_item_id || null,
    elapsed_ms: data?.elapsed_ms ?? null,
  };
}

const TRANSIENT = new Set([
  BUY_SOURCES_FAILURE_REASON.PROVIDER_TIMEOUT,
  BUY_SOURCES_FAILURE_REASON.PROVIDER_HTTP_ERROR,
  BUY_SOURCES_FAILURE_REASON.PROVIDER_RATE_LIMITED,
  BUY_SOURCES_FAILURE_REASON.FRONTEND_ABORTED,
  BUY_SOURCES_FAILURE_REASON.BACKEND_ERROR,
  BUY_SOURCES_FAILURE_REASON.UNKNOWN,
]);

export function isTransientBuySourcesFailure(reason) {
  return TRANSIENT.has(String(reason || ""));
}

/**
 * Classify axios / fetch failures from POST /buy-sources/search.
 */
export function classifyBuySourcesFailure(err, { missingTitle = false } = {}) {
  if (missingTitle) {
    return {
      reason: BUY_SOURCES_FAILURE_REASON.MISSING_QUERY_IDENTITY,
      http_status: null,
      detail_code: null,
    };
  }
  if (!err) {
    return {
      reason: BUY_SOURCES_FAILURE_REASON.UNKNOWN,
      http_status: null,
      detail_code: null,
    };
  }

  const status = err?.response?.status ?? err?.status ?? null;
  const detail = err?.response?.data?.detail;
  const detailObj = detail && typeof detail === "object" ? detail : null;
  const detailCode =
    detailObj?.reason ||
    detailObj?.buy_sources_failure_reason ||
    detailObj?.code ||
    (typeof detail === "string" ? detail : null);
  const providerStatusClass = detailObj?.provider_status_class || null;
  const requestId = detailObj?.request_id || null;
  const walmartItemId = detailObj?.walmart_item_id || null;
  const elapsedMs = detailObj?.elapsed_ms ?? null;
  const code = String(err?.code || "");
  const message = String(err?.message || "").toLowerCase();

  const baseMeta = {
    http_status: status,
    detail_code: detailCode,
    provider_status_class: providerStatusClass,
    request_id: requestId,
    walmart_item_id: walmartItemId,
    elapsed_ms: elapsedMs,
    provider_elapsed_ms: detailObj?.provider_elapsed_ms ?? null,
    total_elapsed_ms: detailObj?.total_elapsed_ms ?? elapsedMs,
    queue_wait_ms: detailObj?.queue_wait_ms ?? null,
  };

  if (code === "ERR_CANCELED" || message.includes("canceled") || message.includes("aborted")) {
    return {
      reason: BUY_SOURCES_FAILURE_REASON.FRONTEND_ABORTED,
      ...baseMeta,
    };
  }

  if (detailCode === BUY_SOURCES_FAILURE_REASON.PROVIDER_UNAVAILABLE) {
    return {
      reason: BUY_SOURCES_FAILURE_REASON.PROVIDER_UNAVAILABLE,
      ...baseMeta,
      provider_status_class: "unavailable",
    };
  }

  if (
    code === "ECONNABORTED" ||
    message.includes("timeout") ||
    status === 504 ||
    detailCode === "buy_sources_timeout" ||
    detailCode === "provider_timeout" ||
    detailCode === "oxylabs_timeout" ||
    providerStatusClass === "timeout"
  ) {
    return {
      reason: BUY_SOURCES_FAILURE_REASON.PROVIDER_TIMEOUT,
      ...baseMeta,
      provider_status_class: providerStatusClass || "timeout",
    };
  }

  if (status === 429 || detailCode === "rate_limited" || detailCode === "provider_rate_limited") {
    return {
      reason: BUY_SOURCES_FAILURE_REASON.PROVIDER_RATE_LIMITED,
      ...baseMeta,
      provider_status_class: providerStatusClass || "rate_limited",
    };
  }

  if (
    status === 422 &&
    (detailCode === "buy_sources_invalid" ||
      detailCode === "invalid" ||
      detailCode === "search_context_required")
  ) {
    return {
      reason: BUY_SOURCES_FAILURE_REASON.MISSING_QUERY_IDENTITY,
      ...baseMeta,
    };
  }

  // Upstream Oxylabs / gateway failures (often HTTP 502 from our route).
  if (
    status === 502 ||
    detailCode === "oxylabs_error" ||
    providerStatusClass === "upstream_error"
  ) {
    return {
      reason: BUY_SOURCES_FAILURE_REASON.PROVIDER_HTTP_ERROR,
      ...baseMeta,
      provider_status_class: providerStatusClass || "upstream_error",
    };
  }

  if (typeof status === "number" && status >= 500) {
    return {
      reason: BUY_SOURCES_FAILURE_REASON.BACKEND_ERROR,
      ...baseMeta,
      provider_status_class: providerStatusClass || "upstream_error",
    };
  }

  if (typeof status === "number" && status >= 400) {
    return {
      reason: BUY_SOURCES_FAILURE_REASON.PROVIDER_HTTP_ERROR,
      ...baseMeta,
    };
  }

  return {
    reason: BUY_SOURCES_FAILURE_REASON.UNKNOWN,
    ...baseMeta,
  };
}

export function buySourcesErrorEntry(baseEmpty, classification) {
  const reason = classification?.reason || BUY_SOURCES_FAILURE_REASON.UNKNOWN;
  return {
    ...baseEmpty,
    error: reason,
    failure_reason: reason,
    buy_sources_failure_reason: reason,
    request_id: classification?.request_id ?? null,
    diagnostics: {
      buy_sources_attempted: true,
      buy_sources_succeeded: false,
      buy_sources_failed: true,
      buy_sources_failure_reason: reason,
      http_status: classification?.http_status ?? null,
      detail_code: classification?.detail_code ?? null,
      provider_status_class: classification?.provider_status_class ?? null,
      request_id: classification?.request_id ?? null,
      walmart_item_id: classification?.walmart_item_id ?? null,
      elapsed_ms: classification?.elapsed_ms ?? null,
      provider_elapsed_ms: classification?.provider_elapsed_ms ?? null,
      total_elapsed_ms: classification?.total_elapsed_ms ?? classification?.elapsed_ms ?? null,
      queue_wait_ms: classification?.queue_wait_ms ?? null,
      transient: isTransientBuySourcesFailure(reason),
    },
  };
}
